import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/* ═══════════════════════════════════════════════════════════
   STORE PRODUCT SERVICE FEE SLICE
═══════════════════════════════════════════════════════════ */

/* ─────────────────────────────────────────────
   RATE TYPES
   Two ways a product is priced, nothing more:
     perItem    — the price is for one unit, so the line
                  multiplies by however many were ordered
     perProduct — the price is for the whole thing as listed
                  (a set, a bundle, a made-to-order piece),
                  so quantity doesn't move the price
───────────────────────────────────────────── */

export type RateType = "perItem" | "perProduct";

export const DEFAULT_RATE_TYPE: RateType = "perItem";

/** How many billing units a quantity comes to. The product-side twin of
    shortstay's nightsToBillingUnits: perProduct always bills once. */
export const quantityToBillingUnits = (qty: number, rateType: RateType = DEFAULT_RATE_TYPE): number => {
  const safeQty = Math.max(0, qty || 0);
  return rateType === "perProduct" ? (safeQty > 0 ? 1 : 0) : safeQty;
};

/** What one cart line actually costs. */
export const calculateLineTotal = (
  rate: number,
  qty: number,
  rateType: RateType = DEFAULT_RATE_TYPE
): number => parseFloat(((rate || 0) * quantityToBillingUnits(qty, rateType)).toFixed(2));

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface StoreCartLineItem {
  listingId: string;
  name: string;
  rate: number;
  rateType?: RateType; // defaults to perItem for anything saved before this
  qty: number;
  hostId: string;     // 🔥 required now — needed to split earnings per seller
  brandId?: string;   // used to attribute the right slice of delivery cost
}

export interface StoreCartExtraItem {
  id: string;
  name: string;
  price: number;
  qty?: number;
}

export interface HostProfitEntry {
  saleTotal: number;
  hostsFee: number;
  deliveryFee: number;
  hostProfit: number;
}

interface StoreProductServiceFeeState {
  items: StoreCartLineItem[];
  extras: StoreCartExtraItem[];

  subtotal: number;
  extrasTotal: number;
  totalQty: number;

  guestsFee: number;
  hostsFee: number;       // aggregate across all hosts (sum of hostProfits[*].hostsFee)
  hostProfit: number;     // aggregate across all hosts (sum of hostProfits[*].hostProfit)
  hostProfits: Record<string, HostProfitEntry>; // 🔥 per-host breakdown, keyed by hostId
  platformProfit: number;

  deliveryFee: number;    // total delivery across all brands
  totalCharged: number;

  smallOrderFee: number;
  cancellationFee: number;
  schedulingFee: number;

  calculated: boolean;
}

/* ─────────────────────────────────────────────
   HOST / GUEST FEE CURVES — identical tiers to the backend
───────────────────────────────────────────── */

/* was a flat 20% — now the same 5-tier ladder the backend runs
   per host, called with that host's own saleTotal */
export const calculateHostEarnings = (total: number) => {
  let percentage: number;
  if (total > 5000) percentage = 0.2;
  else if (total > 1000) percentage = 0.15;
  else if (total > 500) percentage = 0.12;
  else if (total > 100) percentage = 0.1;
  else percentage = 0.08;

  const hostsFee = parseFloat((total * percentage).toFixed(2));
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

/* >1000 tier corrected: was 0.2 (duplicate of the >5000 tier),
   now 0.15 to match the backend exactly */
export const calculateGuestServiceFee = (subtotal: number): number => {
  let guestsFee: number;
  if (subtotal > 5000) guestsFee = subtotal * 0.2;
  else if (subtotal > 1000) guestsFee = subtotal * 0.15;
  else if (subtotal > 500) guestsFee = subtotal * 0.12;
  else if (subtotal > 100) guestsFee = subtotal * 0.1;
  else guestsFee = subtotal * 0.08;
  return parseFloat(guestsFee.toFixed(2));
};

export const calculatePlatformProfit = (
  hostsFee: number,
  guestsFee: number
): number => parseFloat((hostsFee + guestsFee).toFixed(2));

/* ─────────────────────────────────────────────
   NEW FEE CURVES — same ladder shape, scaled ranges
───────────────────────────────────────────── */

/* small-order surcharge: carts under €150 carry a flat 5% fee
   to offset fixed processing cost; clears once the order hits €150 */
export const calculateSmallOrderFee = (total: number): number => {
  if (total > 0 && total < 150) return parseFloat((total * 0.05).toFixed(2));
  return 0;
};

/* cancellation fee — same 5-tier bracket cutoffs as host/guest,
   scaled to a 4%–8% range instead of 8%–20% */
export const calculateCancellationFee = (total: number): number => {
  let percentage: number;
  if (total > 5000) percentage = 0.08;
  else if (total > 1000) percentage = 0.07;
  else if (total > 500) percentage = 0.06;
  else if (total > 100) percentage = 0.05;
  else percentage = 0.04;
  return parseFloat((total * percentage).toFixed(2));
};

/* scheduling fee — same bracket cutoffs again, scaled to 2%–6% */
export const calculateSchedulingFee = (total: number): number => {
  let percentage: number;
  if (total > 5000) percentage = 0.06;
  else if (total > 1000) percentage = 0.05;
  else if (total > 500) percentage = 0.04;
  else if (total > 100) percentage = 0.03;
  else percentage = 0.02;
  return parseFloat((total * percentage).toFixed(2));
};

/* ─────────────────────────────────────────────
   MAIN THUNK — full cart breakdown, per host
───────────────────────────────────────────── */

export const calculateStoreProductsEarnings = createAsyncThunk(
  "storeProductServiceFee/calculateStoreProductsEarnings",
  async ({
    items,
    extras = [],
    deliveryFee = 0,
    brandDeliveryFees,
  }: {
    items: StoreCartLineItem[];
    extras?: StoreCartExtraItem[];
    deliveryFee?: number;              // flat fallback (e.g. FBP carrier rate)
    brandDeliveryFees?: Record<string, number>; // real per-brand zone fees, like the backend
  }) => {
    /* how many units are actually leaving the shelf — a perProduct line
       still ships whatever quantity was ordered, it just doesn't re-price */
    const totalQty = items.reduce((s, i) => s + i.qty, 0);

    const itemsSubtotal = items.reduce(
      (s, i) => s + calculateLineTotal(i.rate, i.qty, i.rateType ?? DEFAULT_RATE_TYPE),
      0
    );
    const extrasTotal = extras.reduce((s, e) => s + (e.qty || 1) * e.price, 0);
    const subtotal = parseFloat((itemsSubtotal + extrasTotal).toFixed(2));

    const deliveryTotal = brandDeliveryFees
      ? parseFloat(Object.values(brandDeliveryFees).reduce((s, f) => s + f, 0).toFixed(2))
      : parseFloat(deliveryFee.toFixed(2));

    /* group items by hostId — mirrors the backend's groupedByHost */
    const groupedByHost: Record<string, StoreCartLineItem[]> = {};
    items.forEach((item) => {
      if (!groupedByHost[item.hostId]) groupedByHost[item.hostId] = [];
      groupedByHost[item.hostId].push(item);
    });

    const hostProfits: Record<string, HostProfitEntry> = {};
    let globalHostsFee = 0;

    for (const hostId in groupedByHost) {
      const hostItems = groupedByHost[hostId];
      const hostSaleTotal = parseFloat(
        hostItems
          .reduce((s, i) => s + calculateLineTotal(i.rate, i.qty, i.rateType ?? DEFAULT_RATE_TYPE), 0)
          .toFixed(2)
      );
      const { hostsFee, hostProfit: profitBeforeDelivery } = calculateHostEarnings(hostSaleTotal);

      /* this host's share of delivery — exact sum of their own brands
         when a real per-brand map is given, else split proportionally
         by sale value (same fallback the backend uses for FBP/FBA) */
      const hostBrandIds = [...new Set(hostItems.map((i) => i.brandId).filter(Boolean))] as string[];
      const hostDeliveryFee = brandDeliveryFees
        ? parseFloat(
            hostBrandIds.reduce((s, bid) => s + (brandDeliveryFees[bid] || 0), 0).toFixed(2)
          )
        : parseFloat((deliveryTotal * (hostSaleTotal / (itemsSubtotal || 1))).toFixed(2));

      const hostProfit = parseFloat((profitBeforeDelivery + hostDeliveryFee).toFixed(2));

      globalHostsFee = parseFloat((globalHostsFee + hostsFee).toFixed(2));
      hostProfits[hostId] = {
        saleTotal: hostSaleTotal,
        hostsFee,
        deliveryFee: hostDeliveryFee,
        hostProfit,
      };
    }

    const aggregateHostProfit = parseFloat(
      Object.values(hostProfits).reduce((s, h) => s + h.hostProfit, 0).toFixed(2)
    );

    const guestsFee = calculateGuestServiceFee(subtotal);
    const platformProfit = calculatePlatformProfit(globalHostsFee, guestsFee);

    // Delivery is passed through to the guest at cost — not marked up,
    // not counted toward host/platform profit.
    const totalCharged = parseFloat((subtotal + guestsFee + deliveryTotal).toFixed(2));

    return {
      items,
      extras,
      totalQty,
      subtotal,
      extrasTotal: parseFloat(extrasTotal.toFixed(2)),

      guestsFee,
      hostsFee: globalHostsFee,
      hostProfit: aggregateHostProfit,
      hostProfits,
      platformProfit,

      deliveryFee: deliveryTotal,
      totalCharged,
    };
  }
);

/* ─────────────────────────────────────────────
   NEW THUNKS — under150 / cancellation / scheduling
───────────────────────────────────────────── */

export const calculateUnder150Fee = createAsyncThunk(
  "storeProductServiceFee/calculateUnder150Fee",
  async ({ total }: { total: number }) => {
    const fee = calculateSmallOrderFee(total);
    return { total, fee };
  }
);

export const calculateStoreCancellationFee = createAsyncThunk(
  "storeProductServiceFee/calculateStoreCancellationFee",
  async ({ total }: { total: number }) => {
    const fee = calculateCancellationFee(total);
    return { total, fee };
  }
);

export const calculateStoreSchedulingFee = createAsyncThunk(
  "storeProductServiceFee/calculateStoreSchedulingFee",
  async ({ total }: { total: number }) => {
    const fee = calculateSchedulingFee(total);
    return { total, fee };
  }
);

/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const initialState: StoreProductServiceFeeState = {
  items: [],
  extras: [],

  subtotal: 0,
  extrasTotal: 0,
  totalQty: 0,

  guestsFee: 0,
  hostsFee: 0,
  hostProfit: 0,
  hostProfits: {},
  platformProfit: 0,

  deliveryFee: 0,
  totalCharged: 0,

  smallOrderFee: 0,
  cancellationFee: 0,
  schedulingFee: 0,

  calculated: false,
};

const storeProductServiceFeeSlice = createSlice({
  name: "storeProductServiceFee",
  initialState,
  reducers: {
    resetStoreProductServiceFees: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(calculateStoreProductsEarnings.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.extras = action.payload.extras;
        state.totalQty = action.payload.totalQty;
        state.subtotal = action.payload.subtotal;
        state.extrasTotal = action.payload.extrasTotal;

        state.guestsFee = action.payload.guestsFee;
        state.hostsFee = action.payload.hostsFee;
        state.hostProfit = action.payload.hostProfit;
        state.hostProfits = action.payload.hostProfits;
        state.platformProfit = action.payload.platformProfit;

        state.deliveryFee = action.payload.deliveryFee;
        state.totalCharged = action.payload.totalCharged;

        state.calculated = true;
      })
      .addCase(calculateUnder150Fee.fulfilled, (state, action) => {
        state.smallOrderFee = action.payload.fee;
      })
      .addCase(calculateStoreCancellationFee.fulfilled, (state, action) => {
        state.cancellationFee = action.payload.fee;
      })
      .addCase(calculateStoreSchedulingFee.fulfilled, (state, action) => {
        state.schedulingFee = action.payload.fee;
      });
  },
});

export const { resetStoreProductServiceFees } = storeProductServiceFeeSlice.actions;

export default storeProductServiceFeeSlice.reducer;